import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { logError } from '@cortex/faro';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { RichTextEditor, type RichTextValue } from '../components/RichTextEditor.js';
import { weeklyReportApi, weeklyReportImageUrl } from '../lib/api.js';
import { prepareReportImage } from '../lib/report-image.js';
import { queryKeys } from '../lib/query-keys.js';
import type { ReportBodyJson, WeeklyReportInput } from '../types/weekly-report.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 今週の月曜日を YYYY-MM-DD で返す（対象週の初期値）。
 * @graph-connects none
 */
function currentMonday(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = (day + 6) % 7;
  now.setDate(now.getDate() - diff);
  const y = String(now.getFullYear());
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 編集中レポートの localStorage 自動保存ドラフト。誤って画面を離れても入力を復元できるようにブラウザ側にだけ保持する（サーバへは保存ボタンを押すまで送らない）。
 * @graph-connects none
 */
interface StoredDraft {
  title: string;
  weekOf: string;
  bodyJson: ReportBodyJson;
  bodyHtml: string;
  savedAt: string;
}

/** @graph-connects none */
const DRAFT_STORAGE_PREFIX = 'ac-mens-hub:weekly-report-draft:';

/** @graph-connects none */
const AUTOSAVE_DEBOUNCE_MS = 800;

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business レポート ID（新規は 'new'）から localStorage の自動保存キーを組み立てる。
 * @graph-connects none
 */
function draftStorageKey(reportId?: string): string {
  return `${DRAFT_STORAGE_PREFIX}${reportId ?? 'new'}`;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business localStorage から自動保存ドラフトを読み出す。壊れた JSON・形式不一致は null（復元候補なし）として扱う。
 * @graph-connects none
 */
function readStoredDraft(key: string): StoredDraft | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredDraft>;
    if (
      typeof parsed.title !== 'string' ||
      typeof parsed.weekOf !== 'string' ||
      typeof parsed.bodyHtml !== 'string' ||
      typeof parsed.savedAt !== 'string' ||
      typeof parsed.bodyJson !== 'object' ||
      parsed.bodyJson === null
    ) {
      return null;
    }
    return parsed as StoredDraft;
  } catch (error: unknown) {
    logError(error instanceof Error ? error : new Error(String(error)), {
      event: 'draft_read_failed',
    });
    return null;
  }
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 自動保存ドラフトを localStorage に書き込む。容量超過（QuotaExceededError）等は自動保存の失敗として黙って握りつぶす（編集操作は妨げない）。
 * @graph-connects none
 */
function writeStoredDraft(key: string, draft: StoredDraft): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch (error: unknown) {
    logError(error instanceof Error ? error : new Error(String(error)), {
      event: 'draft_write_failed',
    });
  }
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 自動保存ドラフトを削除する（サーバ保存成功時・ユーザーが復元を破棄した時）。
 * @graph-connects none
 */
function clearStoredDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch (error: unknown) {
    logError(error instanceof Error ? error : new Error(String(error)), {
      event: 'draft_clear_failed',
    });
  }
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 画像ファイルを週次レポート用に前処理（自動縮小・圧縮）してサーバへアップロードし、本文に埋め込む表示 URL を返す。
 * @graph-connects prepareReportImage [calls] 画像の縮小・base64 化
 * @graph-connects weeklyReportApi [calls] 画像アップロード API
 */
async function uploadReportImage(file: File): Promise<string> {
  const payload = await prepareReportImage(file);
  const { image } = await weeklyReportApi.uploadImage(payload);
  return weeklyReportImageUrl(image.id);
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 週次レポートの作成・編集ページ。タイトル・対象週・本文（tiptap）を編集し、下書き保存または保存して公開する。id があれば編集、無ければ新規作成。編集者のみ到達する想定。
 * @graph-connects weeklyReportApi [calls] 週次レポートの作成・更新・公開 API
 * @graph-connects queryKeys [calls] react-query キーファクトリ
 */
export function WeeklyReportEditPage() {
  const { t } = useTranslation();
  const params = useParams({ strict: false });
  const id = (params as { id?: string }).id;
  const isEdit = typeof id === 'string' && id !== '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.weeklyReports.detail(id ?? ''),
    queryFn: () => weeklyReportApi.get(id ?? ''),
    enabled: isEdit,
  });

  if (isEdit && isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-10 text-sm text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  return (
    <WeeklyReportEditForm
      reportId={isEdit ? id : undefined}
      initialTitle={data?.report.title ?? ''}
      initialWeekOf={data?.report.weekOf ?? currentMonday()}
      initialBodyJson={data?.report.bodyJson}
      initialBodyHtml={data?.report.bodyHtml ?? ''}
      initialUpdatedAt={data?.report.updatedAt}
      onSaved={(savedId) => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.weeklyReports.all });
        void navigate({ to: '/weekly-report/$id', params: { id: savedId } });
      }}
    />
  );
}

interface WeeklyReportEditFormProps {
  reportId?: string;
  initialTitle: string;
  initialWeekOf: string;
  initialBodyJson?: ReportBodyJson;
  initialBodyHtml: string;
  initialUpdatedAt?: string;
  onSaved: (id: string) => void;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 週次レポート編集フォーム本体。ローカル state でタイトル・対象週・本文を保持し、下書き保存／保存して公開を実行する。編集内容はデバウンス付きで localStorage に自動保存し、誤って画面を離れた後の再訪時に復元バナーを出す（サーバのレポート自体は保存ボタンまで書き換えない）。
 * @graph-connects weeklyReportApi [calls] 作成・更新・公開 API
 * @graph-connects uploadReportImage [calls] 本文画像のアップロード
 */
function WeeklyReportEditForm({
  reportId,
  initialTitle,
  initialWeekOf,
  initialBodyJson,
  initialBodyHtml,
  initialUpdatedAt,
  onSaved,
}: WeeklyReportEditFormProps) {
  const { t } = useTranslation();
  const storageKey = draftStorageKey(reportId);
  const [title, setTitle] = useState(initialTitle);
  const [weekOf, setWeekOf] = useState(initialWeekOf);
  const bodyRef = useRef<RichTextValue>({
    bodyJson: initialBodyJson ?? {},
    bodyHtml: initialBodyHtml,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  // サーバ側の最終更新より新しい自動保存ドラフトがあれば復元候補として提示する。
  // （古いドラフトは既にサーバへ保存済みの内容なので黙って捨てる。）
  const [restorable, setRestorable] = useState<StoredDraft | null>(() => {
    const stored = readStoredDraft(draftStorageKey(reportId));
    if (!stored) return null;
    if (initialUpdatedAt !== undefined && stored.savedAt <= initialUpdatedAt) return null;
    return stored;
  });

  // 復元でエディタの初期内容を差し替えるため、key で RichTextEditor を再マウントする。
  const [editorSeed, setEditorSeed] = useState<{ content?: ReportBodyJson; key: number }>({
    content: initialBodyJson,
    key: 0,
  });

  const initialContent = useMemo(() => editorSeed.content, [editorSeed]);

  // 自動保存のデバウンスタイマー。最新の title / weekOf は ref 経由で読む（stale closure 回避）。
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFieldsRef = useRef({ title, weekOf });
  latestFieldsRef.current = { title, weekOf };

  const scheduleAutosave = () => {
    if (autosaveTimerRef.current !== null) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      writeStoredDraft(storageKey, {
        title: latestFieldsRef.current.title,
        weekOf: latestFieldsRef.current.weekOf,
        bodyJson: bodyRef.current.bodyJson,
        bodyHtml: bodyRef.current.bodyHtml,
        savedAt: new Date().toISOString(),
      });
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  // アンマウント時は保留中の自動保存を破棄する（保存成功→遷移後に古い内容を書き戻さない）。
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current !== null) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  const buildInput = (): WeeklyReportInput | null => {
    if (title.trim() === '') {
      setErrorMessage(t('weeklyReport.edit.titleRequired'));
      return null;
    }
    setErrorMessage(null);
    return {
      title: title.trim(),
      weekOf,
      bodyJson: bodyRef.current.bodyJson,
      bodyHtml: bodyRef.current.bodyHtml,
    };
  };

  const saveMutation = useMutation({
    mutationFn: async (publish: boolean) => {
      const input = buildInput();
      if (!input) throw new Error('validation');
      const saved = reportId
        ? await weeklyReportApi.update(reportId, input)
        : await weeklyReportApi.create(input);
      if (publish) {
        await weeklyReportApi.publish(saved.report.id);
      }
      return saved.report.id;
    },
    onSuccess: (savedId) => {
      // サーバ保存に成功したのでローカルの自動保存ドラフトは役目を終える。
      if (autosaveTimerRef.current !== null) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      clearStoredDraft(storageKey);
      onSaved(savedId);
    },
    onError: (e) => {
      if (e instanceof Error && e.message === 'validation') return;
      setErrorMessage(t('weeklyReport.edit.saveError'));
    },
  });

  const restoreDraft = (draft: StoredDraft) => {
    setTitle(draft.title);
    setWeekOf(draft.weekOf);
    bodyRef.current = { bodyJson: draft.bodyJson, bodyHtml: draft.bodyHtml };
    setEditorSeed((seed) => ({ content: draft.bodyJson, key: seed.key + 1 }));
    setRestorable(null);
  };

  const discardDraft = () => {
    clearStoredDraft(storageKey);
    setRestorable(null);
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <button
        type="button"
        onClick={() => {
          void navigate({ to: '/weekly-report' });
        }}
        className="mb-6 inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('common.backToList')}
      </button>

      <h1 className="mb-6 text-2xl font-bold text-[var(--color-text-primary)]">
        {reportId ? t('weeklyReport.edit.editTitle') : t('weeklyReport.edit.createTitle')}
      </h1>

      {restorable ? (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-3">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('weeklyReport.edit.restorePrompt', {
              savedAt: new Date(restorable.savedAt).toLocaleString('ja-JP'),
            })}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                restoreDraft(restorable);
              }}
            >
              {t('weeklyReport.edit.restore')}
            </Button>
            <Button variant="ghost" size="sm" onClick={discardDraft}>
              {t('weeklyReport.edit.discard')}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-4">
        <Input
          label={t('common.title')}
          value={title}
          fullWidth
          placeholder={t('weeklyReport.edit.titlePlaceholder')}
          onChange={(e) => {
            setTitle(e.target.value);
            scheduleAutosave();
          }}
        />
        <Input
          label={t('weeklyReport.edit.targetWeek')}
          type="date"
          value={weekOf}
          onChange={(e) => {
            setWeekOf(e.target.value);
            scheduleAutosave();
          }}
        />

        <div>
          <span className="mb-1.5 block text-sm font-medium text-[var(--color-text-secondary)]">
            {t('weeklyReport.edit.body')}
          </span>
          <RichTextEditor
            key={editorSeed.key}
            initialContent={initialContent}
            uploadImage={uploadReportImage}
            onChange={(value) => {
              bodyRef.current = value;
              scheduleAutosave();
            }}
          />
        </div>

        {errorMessage ? <p className="text-sm text-[var(--color-error)]">{errorMessage}</p> : null}

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            loading={saveMutation.isPending}
            onClick={() => {
              saveMutation.mutate(false);
            }}
          >
            {t('weeklyReport.edit.saveDraft')}
          </Button>
          <Button
            variant="primary"
            loading={saveMutation.isPending}
            onClick={() => {
              saveMutation.mutate(true);
            }}
          >
            {t('weeklyReport.edit.saveAndPublish')}
          </Button>
        </div>
      </div>
    </div>
  );
}
